/** @jsx jsx */
import { jsx } from 'theme-ui'
import { Field, FieldProps, FieldConfig } from 'formik'
import _ from 'lodash'
import React, { useContext } from 'react'
import { useRegisteredRef } from 'react-register-nodes'
import { FormGroup, FormGroupDivider } from './FormGroup'

export interface IConnectedFormFieldChildProps extends FieldProps<any> {
  isFocused: boolean
  hasErrors: boolean
  divContainerProps: { ref?: any; onFocus: () => void; onBlur: () => void }
}

interface IConnectedFormFieldProps extends FieldConfig {
  name: string
  children: (props: IConnectedFormFieldChildProps) => React.ReactElement<any> | null
  analyticsEvent?: 'onBlur' | 'onChange'
  ignoreTouched?: boolean
}

const ConnectedFormFieldContext = React.createContext({ isFocused: false, hasErrors: false })

export const ConnectedFormField: React.SFC<IConnectedFormFieldProps> = ({
  name,
  children,
  analyticsEvent,
  ignoreTouched,
}) => {
  const ref = useRegisteredRef(name)
  const [isFocused, setIsFocused] = React.useState(false)
  return (
    <Field name={name}>
      {({ field, form, meta }: FieldProps<any>) => {
        const error = (ignoreTouched || _.get(form.touched, name)) && _.get(form.errors, name)
        const props: IConnectedFormFieldChildProps = {
          isFocused,
          hasErrors: !!error,
          meta,
          field: {
            ...field,
          },
          form,
          divContainerProps: {
            ref: error ? ref : undefined,
            onFocus: () => {
              setIsFocused(true)
            },
            onBlur: () => {
              setIsFocused(false)
            },
          },
        }
        return (
          <ConnectedFormFieldContext.Provider value={{ isFocused, hasErrors: props.hasErrors }}>
            {children(props)}
          </ConnectedFormFieldContext.Provider>
        )
      }}
    </Field>
  )
}

export const ConnectedFormGroup: React.SFC<
  IConnectedFormFieldProps & React.ComponentPropsWithoutRef<typeof FormGroup>
> = ({ name, analyticsEvent, children, ignoreTouched, ...formGroupProps }) => {
  return (
    <ConnectedFormField {...{ name, children, analyticsEvent, ignoreTouched }}>
      {(props) => (
        <FormGroup
          isFocused={props.isFocused}
          hasErrors={props.hasErrors}
          {...props.divContainerProps}
          {...formGroupProps}
        >
          {children(props)}
        </FormGroup>
      )}
    </ConnectedFormField>
  )
}

export const ConnectedFormGroupDivider: React.SFC<{}> = () => {
  const context = useContext(ConnectedFormFieldContext)
  return <FormGroupDivider {...context} />
}
